import {
  AddRegular,
  DeleteRegular,
  EditRegular,
  EyeRegular,
} from "@fluentui/react-icons";
import {
  adminDeleteProjectIqWorkspace,
  adminGetProjectIqWorkspaces,
  adminUpdateProjectIqWorkspace,
} from "apps/project-iq/api";
import { EditProjectIqWorkspaceDialog } from "apps/project-iq/components/EditProjectIqWorkspaceDialog/EditProjectIqWorkspaceDialog";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { AdminListPage } from "components/adminList/AdminListPage";
import {
  IAdminListAction,
  IAdminRowAction,
} from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { ViewEntityDialog } from "components/viewDialog/ViewEntityDialog";
import { IViewEntityField } from "components/viewDialog/ViewEntityDialog.types";
import { useAdminListPagination } from "hooks/useAdminListPagination";
import { useAdminListSearch } from "hooks/useAdminListSearch";
import { useAdminListSort } from "hooks/useAdminListSort";
import { IProjectIQWorkspace } from "models/IProjectIQWorkspace";
import { ITableColumn } from "models/ITableColumn";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { removeProjectIqWorkspace } from "redux/projectIqWorkspacesSlice";
import { AppDispatch } from "store";
import { useManageIqWorkspacesStyles } from "./ManageIqWorkspaces.styles";

export const ManageIqWorkspaces = (): JSX.Element => {
  const colStyles = useAdminColumnStyles();
  const styles = useManageIqWorkspacesStyles();
  const dispatch = useDispatch<AppDispatch>();
  const [workspaces, setWorkspaces] = useState<IProjectIQWorkspace[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [editWorkspace, setEditWorkspace] =
    useState<IProjectIQWorkspace | null>(null);
  const [viewWorkspace, setViewWorkspace] =
    useState<IProjectIQWorkspace | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<IProjectIQWorkspace | null>(
    null,
  );

  const loadWorkspaces = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminGetProjectIqWorkspaces();
      setWorkspaces(data as IProjectIQWorkspace[]);
    } catch {
      setError("Failed to load workspaces. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadWorkspaces();
  }, [loadWorkspaces]);

  const handleDismiss = useCallback(
    (success: boolean): void => {
      setIsDialogOpen(false);
      setEditWorkspace(null);
      if (success) {
        void loadWorkspaces();
      }
    },
    [loadWorkspaces],
  );

  const handleConfirmDelete = useCallback(async (): Promise<void> => {
    if (!deleteTarget) return;
    try {
      await adminDeleteProjectIqWorkspace(deleteTarget.id);
      dispatch(removeProjectIqWorkspace(deleteTarget.id));
      setDeleteTarget(null);
      void loadWorkspaces();
    } catch {
      setError("Failed to delete workspace. Please try again.");
      setDeleteTarget(null);
    }
  }, [deleteTarget, dispatch, loadWorkspaces]);

  const searchFields = useMemo(
    () => ["name", "clientName", "code"] as (keyof IProjectIQWorkspace)[],
    [],
  );

  const { searchQuery, handleSearchChange, filteredItems } =
    useAdminListSearch<IProjectIQWorkspace>({
      items: workspaces,
      searchFields,
    });

  const fullyFilteredItems = useMemo(() => {
    if (!searchQuery) {
      return filteredItems;
    }

    const lowerQuery = searchQuery.toLowerCase();
    const baseIds = new Set(filteredItems.map((ws) => ws.id));
    const ownerMatches = workspaces.filter(
      (ws) =>
        !baseIds.has(ws.id) &&
        ws.owners?.some((o) =>
          o.displayName?.toLowerCase().includes(lowerQuery),
        ),
    );

    return ownerMatches.length > 0
      ? [...filteredItems, ...ownerMatches]
      : filteredItems;
  }, [filteredItems, searchQuery, workspaces]);

  const {
    sortColumn,
    isSortDescending,
    sortItems,
    onSortColumnChanged,
    onClearColumnChanged,
  } = useAdminListSort<IProjectIQWorkspace>({
    cacheKey: "IqWorkspaces",
    defaultSortColumn: "name",
    defaultDescending: false,
  });

  const columns: ITableColumn<IProjectIQWorkspace>[] = useMemo(
    () => [
      {
        columnId: "name",
        displayValue: "Name",
        className: colStyles.medium,
        renderHeaderCell: () => "Name",
        compare: (a: IProjectIQWorkspace, b: IProjectIQWorkspace) =>
          a.name.localeCompare(b.name),
        renderCell: (item: IProjectIQWorkspace) => (
          <span className={styles.nameCell}>{item.name}</span>
        ),
      },
      {
        columnId: "clientName",
        displayValue: "Client",
        className: colStyles.medium,
        renderHeaderCell: () => "Client",
        compare: (a: IProjectIQWorkspace, b: IProjectIQWorkspace) =>
          (a.clientName ?? "").localeCompare(b.clientName ?? ""),
        renderCell: (item: IProjectIQWorkspace) => (
          <span>{item.clientName ?? "—"}</span>
        ),
      },
      {
        columnId: "code",
        displayValue: "Code",
        className: colStyles.medium,
        renderHeaderCell: () => "Code",
        compare: (a: IProjectIQWorkspace, b: IProjectIQWorkspace) =>
          (a.code ?? "").localeCompare(b.code ?? ""),
        renderCell: (item: IProjectIQWorkspace) =>
          item.code ? (
            <span className={styles.pill}>{item.code}</span>
          ) : (
            <span>—</span>
          ),
      },
      {
        columnId: "owners",
        displayValue: "Owner(s)",
        className: colStyles.normal,
        renderHeaderCell: () => "Owner(s)",
        compare: (a: IProjectIQWorkspace, b: IProjectIQWorkspace) =>
          (a.owners[0]?.displayName ?? "").localeCompare(
            b.owners[0]?.displayName ?? "",
          ),
        renderCell: (item: IProjectIQWorkspace) => (
          <span>{item.owners.map((o) => o.displayName).join(", ")}</span>
        ),
      },
      {
        columnId: "members",
        displayValue: "Members",
        className: colStyles.narrow,
        renderHeaderCell: () => "Members",
        compare: (a: IProjectIQWorkspace, b: IProjectIQWorkspace) =>
          (a.teamMembers?.length ?? 0) - (b.teamMembers?.length ?? 0),
        renderCell: (item: IProjectIQWorkspace) => (
          <span>{item.teamMembers?.length ?? 0} members</span>
        ),
      },
    ],
    [colStyles, styles],
  );

  const rowActions: IAdminRowAction<IProjectIQWorkspace>[] = useMemo(
    () => [
      {
        icon: <EyeRegular />,
        tooltip: "View",
        onClick: (item: IProjectIQWorkspace) => setViewWorkspace(item),
      },
      {
        icon: <EditRegular />,
        tooltip: "Edit",
        onClick: (item: IProjectIQWorkspace) => {
          setEditWorkspace(item);
          setIsDialogOpen(true);
        },
      },
      {
        icon: <DeleteRegular />,
        tooltip: "Delete",
        onClick: (item: IProjectIQWorkspace) => setDeleteTarget(item),
      },
    ],
    [],
  );

  const headerActions: IAdminListAction[] = useMemo(
    () => [
      {
        label: "Add New",
        icon: <AddRegular />,
        appearance: "primary",
        onClick: () => {
          setEditWorkspace(null);
          setIsDialogOpen(true);
        },
      },
    ],
    [],
  );

  const sortedItems = useMemo(
    () => sortItems(fullyFilteredItems, columns),
    [fullyFilteredItems, sortItems, columns],
  );

  const {
    paginatedItems,
    selectedPageIndex,
    pageCount,
    handlePageChange,
    totalItemsCount,
    itemsPerPage,
  } = useAdminListPagination<IProjectIQWorkspace>({
    items: sortedItems,
    itemsPerPage: 15,
  });

  return (
    <>
      <AdminListPage<IProjectIQWorkspace>
        title="Workspaces"
        items={workspaces}
        columns={columns}
        isLoading={loading}
        headerActions={headerActions}
        searchPlaceholder="Search workspaces..."
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        rowActions={rowActions}
        getRowId={(item) => item.id}
        sortColumn={sortColumn}
        isSortDescending={isSortDescending}
        onSortChange={onSortColumnChanged}
        onSortClear={onClearColumnChanged}
        paginatedItems={paginatedItems}
        selectedPageIndex={selectedPageIndex}
        pageCount={pageCount}
        totalItemsCount={totalItemsCount}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        emptyStateMessage={
          error ??
          (searchQuery
            ? "No workspaces match the search."
            : "No workspaces found.")
        }
        actionsWidth="150px"
      />
      <EditProjectIqWorkspaceDialog
        isOpen={isDialogOpen}
        workspace={editWorkspace}
        onDismiss={handleDismiss}
        onSave={adminUpdateProjectIqWorkspace}
      />
      <ActionDialog
        isVisible={deleteTarget !== null}
        title="Delete Workspace"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This cannot be undone.`}
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={handleConfirmDelete}
        onDismiss={() => setDeleteTarget(null)}
      />
      {viewWorkspace && (
        <ViewEntityDialog
          isOpen={viewWorkspace !== null}
          onDismiss={() => setViewWorkspace(null)}
          title="View Workspace"
          compact
          fields={
            [
              { label: "Name", value: viewWorkspace.name },
              { label: "Description", value: viewWorkspace.description || "—" },
              { label: "Client", value: viewWorkspace.clientName || "—" },
              { label: "Code", value: viewWorkspace.code || "—" },
              {
                label: "Owner(s)",
                value:
                  viewWorkspace.owners?.map((o) => o.displayName).join(", ") ||
                  "—",
              },
              {
                label: "Members",
                value: String(viewWorkspace.teamMembers?.length ?? 0),
              },
              {
                label: "Last Updated",
                value: viewWorkspace.updatedAt
                  ? new Date(
                      Number(viewWorkspace.updatedAt) * 1000,
                    ).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : "—",
              },
            ] as IViewEntityField[]
          }
        />
      )}
    </>
  );
};
